import React, { FC, memo, useCallback } from "react";

import {
  ContainerAlias,
  ContainerAliasIcon,
  ContainerUnits,
  ContainerValue,
  TwoColumnContainerWrapper,
} from "./styled";
import { renderElement as getRender } from "../utils/renderElement";
import { ConfigContainerChild, ContainerProps } from "../types";

export const TwoColumnContainer: FC<ContainerProps> = memo(
  ({ config, elementConfig, layerInfo, attributes, type, renderElement }) => {
    const { attributes: renderAttributes } = elementConfig?.options || {};

    const renderContainer = useCallback(
      (element: ConfigContainerChild, attribute?: string) => {
        const { options, style, children } = element || {};
        const { hideEmpty, innerTemplateStyle } = options || {};
        const hasUnits = children?.some(({ id }) => id === "units");
        const iconIndex = children?.findIndex(({ id }) => id === "icon");
        const icon = children?.[iconIndex];
        const hasIcon = !!icon;
        const elementChildren = element.children?.map(child => ({
          ...child,
          attributeName: attribute,
          options: { noUnits: hasUnits, ...child.options },
        }));
        const attr = attribute
          ? layerInfo?.layerAttributes?.find(({ attributeName }) => attributeName === attribute)
          : null;

        if (hasIcon) {
          elementChildren[iconIndex] = {
            ...elementChildren[iconIndex],
            type: attr?.icon?.type?.toLowerCase(),
            value: attr?.icon?.resourceId || attr?.icon?.url || attr?.icon?.iconName,
            attributeName: null,
          };
        }

        const render = attribute
          ? getRender({
              config,
              elementConfig: {
                ...element,
                children: elementChildren,
              },
              attributes,
              layerInfo,
              type,
            })
          : renderElement;

        const value = render({ id: "value" });

        if (!value && hideEmpty) return null;

        return (
          <TwoColumnContainerWrapper key={attribute} style={innerTemplateStyle || style}>
            <ContainerAlias>
              {hasIcon && <ContainerAliasIcon>{render({ id: "icon" })}</ContainerAliasIcon>}
              {render({ id: "alias" })}
              {render({ id: "tooltip" })}
            </ContainerAlias>
            <ContainerValue big>
              {value}
              {hasUnits && <ContainerUnits>{render({ id: "units" })}</ContainerUnits>}
            </ContainerValue>
          </TwoColumnContainerWrapper>
        );
      },
      [attributes, config, layerInfo, renderElement, type],
    );

    return renderAttributes?.length ? (
      <>{renderAttributes.map(attribute => renderContainer(elementConfig, attribute))}</>
    ) : (
      renderContainer(elementConfig)
    );
  },
);
